/**
 * A criterion's validations — the SAME structured rules as a transaction field
 * (Built-in / Expression / Business Logic / Call API, severity E/W/P), edited
 * with the shared ValidationRuleEditor in its `criteria` context. Used by the
 * visual (CriteriaCard), dashboard (global criterion) and report criteria.
 *
 * The server runs them on the criteria before the object runs
 * (`twasta_runtime_engine/criteria_validation.py`, Java CriteriaValidation): an
 * error stops the run and is shown under the criterion; a warning / prompt is
 * shown and the run goes ahead. `{form.X}` reads another criterion by name.
 * Plan: docs/plans/criteria-mandatory-and-validations.md.
 */

import { useState } from 'react'
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react'
import type { Validation, ValidationRule } from '@twasta/page-runtime/txn/types'
import { validationSummary } from '@twasta/page-runtime/txn/types'
import ValidationRuleEditor from '@/pages/designers/transaction/ValidationRuleEditor'

const SEVERITY: Record<string, { label: string; color: string }> = {
  E: { label: 'Error — stops the run', color: 'var(--color-error)' },
  W: { label: 'Warning — the run goes ahead', color: 'var(--color-warning)' },
  P: { label: 'Prompt — information only', color: 'var(--color-accent)' },
}

interface Props {
  validations: Validation[] | undefined
  onChange: (next: Validation[]) => void
  project: string
  /** Every criterion's name — offered as {form.X} tokens / pickers. */
  criteriaNames: string[]
  /** The criterion these rules belong to (the editor title). */
  label?: string
  onOpenCode?: (method: string) => void
}

function asRule(v: Validation): ValidationRule | null {
  return v && typeof v === 'object' ? (v as ValidationRule) : null
}

const rowBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 3, border: 'none', background: 'none', cursor: 'pointer',
  color: 'var(--color-text-secondary)',
}

export default function CriteriaValidationsEditor({
  validations, onChange, project, criteriaNames, label, onOpenCode,
}: Props) {
  // null = closed; -1 = adding; >= 0 = editing that index.
  const [editIdx, setEditIdx] = useState<number | null>(null)
  const list = validations || []

  const move = (i: number, d: number) => {
    const j = i + d
    if (j < 0 || j >= list.length) return
    const next = list.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }
  const onSave = (val: Validation) => {
    if (editIdx == null) return
    const next = list.slice()
    if (editIdx < 0) next.push(val)
    else next[editIdx] = val
    onChange(next)
    setEditIdx(null)
  }

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {list.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          No checks. Add one to stop the run on a value that makes no sense —
          a range, a value that must exist in a master, an expression over the
          criteria, business logic or an API call.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 6 }}>
          {list.map((v, i) => {
            const r = asRule(v)
            const sevKey = String(r?.severity || 'E')
            const sev = SEVERITY[sevKey] || SEVERITY.E
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px',
                borderBottom: i < list.length - 1 ? '1px solid var(--color-border)' : undefined,
                opacity: r?.disabled ? 0.55 : 1,
              }}>
                <span title={sev.label} style={{
                  fontSize: 13, fontWeight: 700, padding: '0 6px', borderRadius: 9,
                  border: `1px solid ${sev.color}`, color: sev.color, flexShrink: 0,
                }}>{sevKey}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r?.name || r?.message || '(unnamed)'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {validationSummary(v)}
                  </div>
                </div>
                <button type="button" style={rowBtn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up"><ChevronUp size={13} /></button>
                <button type="button" style={rowBtn} disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Move down"><ChevronDown size={13} /></button>
                <button type="button" style={rowBtn} onClick={() => setEditIdx(i)} aria-label="Edit check"><Pencil size={13} /></button>
                <button type="button" style={{ ...rowBtn, color: 'var(--color-error)' }}
                  onClick={() => onChange(list.filter((_, idx) => idx !== i))} aria-label="Delete check">
                  <Trash2 size={13} />
                </button>
              </div>
            )
          })}
        </div>
      )}
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditIdx(-1)}
        style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Plus size={13} /> Add a check
      </button>

      {editIdx !== null && (
        <ValidationRuleEditor
          open
          onClose={() => setEditIdx(null)}
          value={editIdx >= 0 ? list[editIdx] : ''}
          onSave={onSave}
          formColumnNames={criteriaNames}
          formList={[]}
          project={project}
          title={label ? `Check on ${label}` : 'Check'}
          onOpenCode={onOpenCode}
          context="criteria"
        />
      )}
    </div>
  )
}
